import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import {
  PLOT_HEIGHT,
  cardState,
  spanClass,
  ticksFor,
  type WidgetBlockProps,
} from '../shared/cardShell';

export type EngagementGrowthView = {
  /** `Biggest mover` (read off the indexed series) and `Channels tracked`. */
  kpis: KPIMetric[];
  /** Each channel indexed to 100 at its first non-zero bucket. */
  rows: ChartRow[];
  xKey: string;
  /** One line per channel present in scope. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
  /** The engagement count behind an indexed point, printed beside it in the tooltip. */
  formatDetail?: (row: ChartRow, key: string) => string | undefined;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type EngagementGrowthCardProps = WidgetBlockProps & {
  view?: EngagementGrowthView;
  /** Opens the conversations behind a point (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `849:29599` — every channel indexed to 100 at the start of the period,
 * so channel size cannot distort the shape.
 *
 * The biggest mover is read off the *indexed* series, which is the point of
 * indexing: on raw volume it would always name whichever channel is largest,
 * not whichever one moved — and off the last bucket each series actually has
 * data in, so a series that merely stops reporting is not announced as a total
 * collapse.
 */
export function EngagementGrowthCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: EngagementGrowthCardProps) {
  /* No series means no rows in scope — the channels are the ones present. */
  const hasData = view !== undefined && view.series.length > 0 && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Relative engagement growth by channel"
      helpText="Engagement trends for each channel, indexed to 100 at the start of the period. Values above 100 indicate growth from that starting level."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            threshold={100}
            xTicksTarget={ticksFor(span)}
            formatValue={view.formatValue}
            formatDetail={view.formatDetail}
            formatCompare={view.formatCompare}
          />
        )
      }
      /* Split by channel — Figma's `Orientation=Platforms` legend. */
      legend={view && <ChartLegend items={legendItems(view.series)} orientation="platforms" />}
    />
  );
}
